import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { UsersThree, Check } from "@phosphor-icons/react";

/** Edit the sous-équipes membership for one user. */
export default function UserTeamsDialog({ open, onOpenChange, user, onSaved }) {
  const [availableTeams, setAvailableTeams] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(user?.teams || []);
    api.get("/teams").then((r) => setAvailableTeams(r.data.teams || [])).catch(() => setAvailableTeams([]));
  }, [open, user]);

  const toggle = (t) => setSelected((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}`, { teams: selected });
      onOpenChange(false);
      setTimeout(() => toast.success("Sous-équipes mises à jour"), 50);
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm bg-[#111] border-white/10 text-white rounded-2xl" data-testid="user-teams-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg flex items-center gap-2">
            <UsersThree size={20} className="text-[#005CFF]" weight="duotone" />
            Sous-équipes — {user?.name}
          </DialogTitle>
          <DialogDescription className="text-[11px] uppercase tracking-widest text-white/50 mt-1">
            Choisir les métiers de cet équipier
          </DialogDescription>
        </DialogHeader>

        {availableTeams.length === 0 ? (
          <div className="text-sm text-white/50 py-4">
            Aucune sous-équipe configurée. Ajoute-les depuis Réglages → Sous-équipes.
          </div>
        ) : (
          <div className="space-y-1.5">
            {availableTeams.map((t) => {
              const isOn = selected.includes(t);
              return (
                <button
                  key={t}
                  onClick={() => toggle(t)}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-colors ${isOn ? "bg-[#005CFF]/20 border-[#005CFF]/50" : "bg-white/5 border-white/10 hover:border-white/25"}`}
                  data-testid={`toggle-team-${t}`}
                >
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center ${isOn ? "bg-[#005CFF]" : "bg-white/10"}`}>
                    {isOn && <Check size={12} weight="bold" />}
                  </div>
                  <span className="text-sm">{t}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex gap-2 mt-2">
          <Button variant="ghost" className="flex-1 border border-white/10" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="flex-1 bg-[#005CFF] hover:bg-[#0047CC]" data-testid="save-teams-btn">
            {saving ? "…" : "Enregistrer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
